import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {Redirect} from 'react-router-dom';
import {Button, Header, List, Loader, Message, Segment} from 'semantic-ui-react';

import {useFeatureFlags} from '../../hooks/useFeatureFlags';
import {featureToggleUtil} from '../../utils';
import {McpGrant, listMcpGrants, revokeMcpGrant} from '../../services/mcpApi';

export const isMcpFeatureAvailable = (
  flags: Record<string, boolean> | undefined,
  flagsLoading: boolean,
  userProfile: any
): boolean => {
  if (flagsLoading) return false;
  if (!userProfile) return false;
  if (!featureToggleUtil.hasFeatureAccess(userProfile, 'mcp_api')) return false;
  return Boolean(
    flags?.mcp_api === true || flags?.mcp_api_beta_access_allowed === true
  );
};

type IntegrationsViewProps = {
  eligible: boolean;
  loading: boolean;
  grants: McpGrant[];
  revokingId: string | null;
  error?: string;
  onRevoke: (grantId: string) => void;
};

const scopeLabel = (scope: string): string =>
  scope === 'messages:read'
    ? 'Read messages'
    : scope === 'messages:write'
      ? 'Prepare reply drafts'
      : scope === 'admin:usage:read'
        ? 'Admin analytics'
      : scope === 'admin:moderation:read'
        ? 'Read moderation cases'
      : scope === 'admin:moderation:write'
        ? 'Moderation decisions'
      : scope;

export const IntegrationsView: React.FC<IntegrationsViewProps> = ({
  eligible,
  loading,
  grants,
  revokingId,
  error,
  onRevoke
}) => {
  const {t} = useTranslation();
  return (
    <Segment padded>
      <Header as="h1">{t('McpIntegrations', 'Integrations')}</Header>
      <p>
        {t(
          'McpIntegrationsDescription',
          'Connect OpenAI, Anthropic, or another compatible MCP client to your BetaReader messages.'
        )}
      </p>
      <Message info>
        {t(
          'McpNoManuscriptAccess',
          'Connected apps cannot access manuscript text through MCP.'
        )}
      </Message>
      {!eligible && (
        <Message
          warning
          content={t(
            'McpUnavailable',
            'MCP integrations are available to eligible paid account owners and administrators.'
          )}
        />
      )}
      {error && <Message negative content={error} />}
      {loading && <Loader active inline="centered" />}
      {!loading && grants.length === 0 && (
        <p>{t('McpNoConnectedApps', 'No connected apps.')}</p>
      )}
      {grants.length > 0 && (
        <List divided relaxed aria-label={t('McpConnectedApps', 'Connected apps')}>
          {grants.map(grant => (
            <List.Item key={grant.id}>
              <List.Content floated="right">
                <Button
                  type="button"
                  basic
                  negative
                  loading={revokingId === grant.id}
                  disabled={revokingId !== null}
                  content={t('McpRevoke', 'Revoke')}
                  onClick={() => onRevoke(grant.id)}
                />
              </List.Content>
              <List.Content>
                <List.Header>{grant.clientName}</List.Header>
                <List.Description>
                  {grant.scopes.map(scope => t(`McpScopeShort_${scope}`, scopeLabel(scope))).join(', ')}
                  {grant.lastUsedAt
                    ? ` · ${t('McpLastUsed', 'Last used')}: ${new Date(
                        grant.lastUsedAt
                      ).toLocaleString()}`
                    : ''}
                </List.Description>
              </List.Content>
            </List.Item>
          ))}
        </List>
      )}
    </Segment>
  );
};

export const canManageMcpForAccount = (
  userProfile: any,
  activeAccount: any
): boolean => {
  return Boolean(
    canOpenMcpIntegrationSettings(userProfile, activeAccount) &&
      activeAccount.product?.features?.canUseMcp === true
  );
};

/**
 * Account role gate only. Callers must also require the personal mcp_api
 * feature flag before showing any MCP UI. Grant creation/use is always
 * re-authorized by the API's paid entitlement and feature-gate checks.
 */
export const canOpenMcpIntegrationSettings = (
  userProfile: any,
  activeAccount: any
): boolean => {
  if (!userProfile || !activeAccount) return false;
  const ownerId = activeAccount.owner?._id ?? activeAccount.owner;
  const membership = userProfile.accountMembership?.find(
    (entry: any) =>
      (entry.account?._id ?? entry.account) === activeAccount._id
  );
  return ownerId === userProfile._id || membership?.role === 'admin';
};

const Integrations: React.FC = () => {
  const userProfile = useSelector((state: any) => state.user.userProfile);
  const activeAccount = useSelector((state: any) => state.account.activeAccount);
  const {flags, isLoading: flagsLoading} = useFeatureFlags();
  const featureEnabled = isMcpFeatureAvailable(
    flags,
    flagsLoading,
    userProfile
  );
  const canOpenSettings =
    (featureEnabled && canOpenMcpIntegrationSettings(userProfile, activeAccount)) ||
    (userProfile?.isSuperAdmin === true &&
      (activeAccount?.owner?._id ?? activeAccount?.owner) === userProfile?._id);
  const eligible = canOpenSettings && canManageMcpForAccount(userProfile, activeAccount);
  const [grants, setGrants] = useState<McpGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let current = true;
    if (!canOpenSettings) {
      setGrants([]);
      setLoading(false);
      return () => {
        current = false;
      };
    }
    setLoading(true);
    listMcpGrants()
      .then(value => {
        if (current) setGrants(value);
      })
      .catch(reason => {
        if (current) setError(reason.message);
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [canOpenSettings, userProfile?._id]);

  const revoke = async (grantId: string) => {
    setRevokingId(grantId);
    setError(undefined);
    try {
      await revokeMcpGrant(grantId);
      setGrants(current => current.filter(grant => grant.id !== grantId));
    } catch (reason: any) {
      setError(reason.message);
    } finally {
      setRevokingId(null);
    }
  };

  if (flagsLoading || !userProfile) return <Loader active inline="centered" />;
  if (!canOpenSettings) return <Redirect to="/account/settings" />;

  return (
    <IntegrationsView
      eligible={eligible}
      loading={loading}
      grants={grants}
      revokingId={revokingId}
      error={error}
      onRevoke={revoke}
    />
  );
};

export default Integrations;
